/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/* eslint-disable @typescript-eslint/no-shadow */

import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { FEEDBACK_FILTER_LIMIT } from "components/search/searchBarConstants"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory, FilterTrigger } from "constants/tracking"
import { BusinessType, MerchantStoreInfo } from "types/models/menu"
import { actionCreator } from "store/duck/menu"
import { ServiceType, ServiceTypeOptions } from "constants/feedback"
import { MerchantAggregate } from "types/api"
import {
  flattenInfiniteOutlets,
  useSearchOutletsPaginatedInfiniteQuery,
} from "queries/menuApi"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"

export type FeedbackStoreFilter = {
  search?: string
  city?: number
  businessType?: BusinessType
  serviceType?: ServiceType
}

const useFeedbackStorelist = (
  eventCategory: EventCategory = EventCategory.feedbackHomePage,
) => {
  const dispatch = useDispatch()
  const {
    flags: { searchOutlets: isSearchOutletsRtkEnabled },
  } = useRtkQueryMigrationEndpointsFlag()

  const [filter, setFilter] = useState<{
    search: string
    city?: string[]
    businessType?: BusinessType[]
    deliverOption?: string[]
    modelType?: string
    limit: number
  }>({
    search: "",
    limit: FEEDBACK_FILTER_LIMIT,
    modelType: "integrated",
  })

  // Stores tracking context for the pending filter change.
  // Fired in useEffect once the first page of results arrives.
  const pendingTrackRef = useRef<{
    filterObj: FeedbackStoreFilter
  } | null>(null)

  const {
    menu: {
      merchantDeliverOptions,
      outletsSearch: outletsSearchRedux = [],
      isFetchingOutletsSearch: isFetchingOutletsSearchRedux,
    },
  } = useSelector((state: AppState) => state)

  const {
    data: outletsInfiniteData,
    isLoading: isFetchingOutletsSearchRTK,
  } = useSearchOutletsPaginatedInfiniteQuery(filter, {
    skip: !isSearchOutletsRtkEnabled,
  })

  // Use RTK Query state or Redux state based on feature flag
  const outletsSearch = isSearchOutletsRtkEnabled
    ? flattenInfiniteOutlets(outletsInfiniteData)
    : outletsSearchRedux
  const isFetchingOutletsSearch = isSearchOutletsRtkEnabled
    ? isFetchingOutletsSearchRTK
    : isFetchingOutletsSearchRedux

  // Fire tracking once the first page of results arrives after a filter change
  useEffect(() => {
    if (!isSearchOutletsRtkEnabled) return
    if (!pendingTrackRef.current) return
    if (!outletsInfiniteData?.pages?.[0]) return

    const { filterObj } = pendingTrackRef.current
    pendingTrackRef.current = null

    const merchantStore: MerchantStoreInfo[] =
      outletsInfiniteData.pages[0].merchants || []
    TroyWebTracker.sendEvent({
      category: eventCategory,
      action: EventAction.searchStore,
      labels: {
        FILTER_TEXT: filterObj.search,
        FILTER_CITY: filterObj.city,
        FILTER_BUSINESS_TYPE: filterObj.businessType,
        FILTER_DELIVER_OPTION: filterObj.serviceType,
        TRIGGER: FilterTrigger.searchInput,
        RESULT: merchantStore.map(item => item.merchantName).toString(),
        TOTAL_RESULT: merchantStore.length,
      },
    })
  }, [isSearchOutletsRtkEnabled, outletsInfiniteData, eventCategory])

  const filteredList = (() => {
    if (isFetchingOutletsSearch) return []
    return outletsSearch
  })()

  const handleFilter = useCallback(
    async (filterObj: FeedbackStoreFilter) => {
      const getDeliverOption = (value: ServiceType) => {
        if (value === ServiceType.ALL) return undefined

        const deliverOption = merchantDeliverOptions?.map(
          ({ deliverOption }) => deliverOption,
        )

        if (value === ServiceType.DELIVERY)
          return deliverOption?.filter(
            item =>
              item?.includes(ServiceTypeOptions.DELIVERY) ||
              item?.includes(ServiceTypeOptions.TAKEAWAY),
          )

        return deliverOption?.filter(item =>
          item?.includes(ServiceTypeOptions.DINEIN),
        )
      }

      let body: {
        search: string
        limit: number
        modelType?: string
        city?: string[]
        businessType?: BusinessType[]
        deliverOption?: string[]
      } = {
        search: (filterObj?.search || "").trim(),
        limit: FEEDBACK_FILTER_LIMIT,
      }

      if (eventCategory === EventCategory.feedbackHomePage) {
        body = {
          ...body,
          city: [`${filterObj.city}`],
          businessType: [filterObj.businessType || BusinessType.Food],
          deliverOption: getDeliverOption(
            filterObj.serviceType || ServiceType.ALL,
          ),
        }
      }

      if (isSearchOutletsRtkEnabled) {
        // RTK Query path: update filter state so the infinite query re-fetches
        // from page 0 automatically. Tracking fires in the useEffect above
        // once the first page of results arrives.
        body = {
          ...body,
          modelType: "integrated",
        }

        pendingTrackRef.current = { filterObj }
        setFilter(body)
        return
      }

      // Old Redux action path
      const res: any = await dispatch(actionCreator.searchOutlets(body))
      const merchantStore: MerchantStoreInfo[] =
        res?.payload?.data?.merchants || []

      TroyWebTracker.sendEvent({
        category: eventCategory,
        action: EventAction.searchStore,
        labels: {
          FILTER_TEXT: filterObj.search,
          FILTER_CITY: filterObj.city,
          FILTER_BUSINESS_TYPE: filterObj.businessType,
          FILTER_DELIVER_OPTION: filterObj.serviceType,
          TRIGGER: FilterTrigger.searchInput,
          RESULT: merchantStore.map(item => item.merchantName).toString(),
          TOTAL_RESULT: merchantStore.length,
        },
      })
    },
    [
      isSearchOutletsRtkEnabled,
      dispatch,
      merchantDeliverOptions,
      eventCategory,
    ],
  )

  useEffect(() => {
    dispatch(
      actionCreator.fetchMerchantAggregate(
        MerchantAggregate.cityID,
        MerchantAggregate.businessType,
        MerchantAggregate.deliverOption,
      ),
    )

    dispatch(actionCreator.fetchItemsWithoutPhotoCount())
  }, [dispatch])

  return {
    handleFilter,
    filteredList,
    isFetchingOutletsSearch,
  }
}

export default useFeedbackStorelist
